import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Figure } from '../../../../components/common/page/Figure';
import type { BoardScore, ScreenerEvent } from '../api/boardTypes';
import { netCapital, netSide, positionLabel } from '../api/netCapital';
import type { SideLabels } from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { formatUsd } from './cardFormat';
import { FIGURE_ROW_HEIGHT } from './cardLayout';

export function cardTailSx(columns: number) {
    return {
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        columnGap: 1,
        pt: '5px',
        borderTop: 1,
        borderColor: 'divider',
        overflow: 'hidden',
    } as const;
}

export const cardTailStyle = { height: FIGURE_ROW_HEIGHT } as const;

interface CardTailProps {
    columns: number;
    children: ReactNode;
    testId?: string;
}

export function CardTail({ columns, children, testId }: CardTailProps) {
    return (
        <Box
            data-testid={testId}
            style={cardTailStyle}
            sx={cardTailSx(columns)}
        >
            {children}
        </Box>
    );
}

export function TailNote({
    children,
    testId,
}: {
    children: ReactNode;
    testId?: string;
}) {
    return (
        <Typography
            data-testid={testId}
            sx={{
                gridColumn: '1 / -1',
                alignSelf: 'center',
                fontSize: '11px',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {children}
        </Typography>
    );
}

function tradersText(n: number): string {
    return `${n} ${n === 1 ? 'trader' : 'traders'}`;
}

interface FlowFiguresProps {
    score: BoardScore;
    labels: SideLabels;
    event?: Pick<ScreenerEvent, 'markets'> | null;
}

export function FlowFigures({ score, labels, event }: FlowFiguresProps) {
    const theme = useTheme();
    const side = netSide(score);
    const position = positionLabel(event, score, labels);
    return (
        <>
            <Figure
                dense
                label="Net capital"
                value={formatUsd(Math.abs(netCapital(score)))}
                color={sideColor(labels, side, theme)}
                sub={position}
                title={`Net capital ${formatUsd(Math.abs(netCapital(score)))} · ${position}`}
            />
            <Figure
                dense
                label={labels.yes.text}
                value={formatUsd(score.capital_usd_yes)}
                color={
                    side === 'YES'
                        ? sideColor(labels, 'YES', theme)
                        : theme.palette.text.secondary
                }
                sub={tradersText(score.n_traders_yes)}
                title={labels.yes.title}
            />
            <Figure
                dense
                label={labels.no.text}
                value={formatUsd(score.capital_usd_no)}
                color={
                    side === 'NO'
                        ? sideColor(labels, 'NO', theme)
                        : theme.palette.text.secondary
                }
                sub={tradersText(score.n_traders_no)}
                title={labels.no.title}
            />
        </>
    );
}
